/**
 * Pipeline Context - Daily Work Pipeline V1
 *
 * Provides global state for pipeline mode:
 * - Pipeline enabled/disabled status
 * - Day session state (OPEN, CLOSED, or none)
 * - Work item counts
 * - Actions: openDay, closeDay, refresh, togglePipeline
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { pipelineAPI } from '../../api/pipeline.js';
import { useAuth } from './AuthContext.jsx';

const PipelineContext = createContext(null);

/**
 * Pipeline Provider
 * Wraps the app to provide pipeline state and actions
 */
export function PipelineProvider({ children }) {
  const { isAuthenticated } = useAuth();
  // Pipeline settings
  const [pipelineEnabled, setPipelineEnabled] = useState(false);
  const [schedule, setSchedule] = useState(null);

  // Day session state
  const [session, setSession] = useState(null);
  const [counts, setCounts] = useState({
    total: 0,
    p0: 0,
    p1: 0,
    p2: 0,
    open: 0,
    in_progress: 0,
    blocked: 0
  });

  // Loading states
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  // Current date/time from server
  const [currentDate, setCurrentDate] = useState(null);
  const [currentTime, setCurrentTime] = useState(null);

  /**
   * Fetch tenant settings
   */
  const fetchSettings = useCallback(async () => {
    try {
      const response = await pipelineAPI.getTenantSettings();
      const data = response?.data || response;
      setPipelineEnabled(data.pipeline_enabled);
      setSchedule(data.schedule);
      return data;
    } catch (err) {
      // Pipeline settings may not exist yet — this is expected, not an error
      if (process.env.NODE_ENV === 'development') {
        console.debug('[Pipeline] Settings not available:', err.message);
      }
      return null;
    }
  }, []);

  /**
   * Fetch today's session status
   */
  const fetchTodaySession = useCallback(async () => {
    try {
      const response = await pipelineAPI.getTodaySession();
      const data = response?.data || response;
      setSession(data.session);
      setCounts(prev => data.counts || prev);
      setCurrentDate(data.date);
      setCurrentTime(data.current_time);
      setSchedule(data.schedule);
      return data;
    } catch (err) {
      console.error('Failed to fetch today session:', err);
      setError(err.message);
      return null;
    }
  }, []);

  /**
   * Initial load - only when authenticated
   * CRITICAL FIX: Only depend on isAuthenticated, not on callbacks
   * This prevents infinite loops when callbacks are recreated
   */
  useEffect(() => {
    async function init() {
      if (!isAuthenticated) {
        // Not logged in - reset state and skip fetch
        setPipelineEnabled(false);
        setSession(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const settings = await fetchSettings();

        if (settings?.pipeline_enabled) {
          await fetchTodaySession();
        }
      } catch (err) {
        console.error('[PipelineContext] Initialization error:', err);
        // Don't block app load - pipeline is optional
        setPipelineEnabled(false);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // ONLY depend on isAuthenticated, not callbacks

  /**
   * Toggle pipeline mode (admin only)
   */
  const togglePipeline = useCallback(async (enabled) => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await pipelineAPI.updateTenantSettings({ pipeline_enabled: enabled });
      const result = response?.data || response;
      setPipelineEnabled(result.pipeline_enabled);
      setSchedule(result.schedule);

      if (result.pipeline_enabled) {
        await fetchTodaySession();
      } else {
        setSession(null);
      }

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [fetchTodaySession]);

  /**
   * Update schedule settings (admin only)
   */
  const updateSchedule = useCallback(async (scheduleSettings) => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await pipelineAPI.updateTenantSettings({ schedule: scheduleSettings });
      const result = response?.data || response;
      setSchedule(result.schedule);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  /**
   * Open today's day session
   */
  const openDay = useCallback(async () => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await pipelineAPI.openDay();
      const result = response?.data || response;
      setSession(result.day_session);
      setCounts(prev => result.counts || prev);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  /**
   * Close today's day session
   */
  const closeDay = useCallback(async () => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await pipelineAPI.closeDay();
      const result = response?.data || response;
      setSession({ ...session, status: 'CLOSED' });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [session]);

  /**
   * Refresh: re-run rule engine
   */
  const refresh = useCallback(async () => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await pipelineAPI.refresh();
      const result = response?.data || response;
      setCounts(prev => result.counts || prev);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  /**
   * Refetch today's session (for external updates)
   */
  const refetch = useCallback(async () => {
    if (pipelineEnabled) {
      await fetchTodaySession();
    }
  }, [pipelineEnabled, fetchTodaySession]);

  // Computed states
  const isSessionOpen = session?.status === 'OPEN';
  const isSessionClosed = session?.status === 'CLOSED';
  const hasNoSession = !session;

  // Session state for UI
  const sessionState = hasNoSession ? 'NO_SESSION' :
                       isSessionOpen ? 'SESSION_OPEN' :
                       'SESSION_CLOSED';

  // CRITICAL FIX: Memoize value to prevent infinite re-renders
  // Without useMemo, a new object is created on every render, causing all consumers to re-render
  const value = React.useMemo(() => ({
    // Pipeline settings
    pipelineEnabled,
    schedule,

    // Day session
    session,
    sessionState,
    isSessionOpen,
    isSessionClosed,
    hasNoSession,

    // Counts
    counts,

    // Date/time
    currentDate,
    currentTime,

    // Loading/error
    loading,
    actionLoading,
    error,

    // Actions
    togglePipeline,
    updateSchedule,
    openDay,
    closeDay,
    refresh,
    refetch
  }), [
    pipelineEnabled,
    schedule,
    session,
    sessionState,
    isSessionOpen,
    isSessionClosed,
    hasNoSession,
    counts,
    currentDate,
    currentTime,
    loading,
    actionLoading,
    error,
    togglePipeline,
    updateSchedule,
    openDay,
    closeDay,
    refresh,
    refetch
  ]);

  return (
    <PipelineContext.Provider value={value}>
      {children}
    </PipelineContext.Provider>
  );
}

/**
 * Hook to access pipeline context
 */
export function usePipeline() {
  const context = useContext(PipelineContext);

  if (!context) {
    throw new Error('usePipeline must be used within a PipelineProvider');
  }

  return context;
}

/**
 * Hook to check if pipeline is available (optional)
 * Returns null if context not available (instead of throwing)
 */
export function usePipelineOptional() {
  return useContext(PipelineContext);
}

export default PipelineContext;
